import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  FormControlLabel,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TimeSelector from './timeSelector';
import CustomCheckbox from '../CustomCheckbox';
import { useTranslation } from 'react-i18next';
import { createSalary, getSalaryById, updateSalary } from '../../api/salary-managemet';
import { useForm, Controller } from 'react-hook-form';
import { FormInputText } from '../../form-components/FormInputText';
import {
  changeDateAroundMonths,
  dateConverter,
  dateFormatter,
  debounce,
  errorMessages,
  exportUsersToExcel,
  getAge,
  getInstructors,
  validateDateRange
} from '../../utils/methods';
import { useToast } from 'use-toast-mui';
import CommonDatePicker from '../../form-components/CommonDatePicker';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

const currentDate = new Date();
const year = currentDate.getFullYear();
const month = String(currentDate.getMonth() + 1).padStart(2, '0');
const day = String(currentDate.getDate()).padStart(2, '0');
const formattedDate = `${year}-${month}-${day}`;

const PayrollCreate = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleSubmit, control, reset, getValues, watch, setValue } = useForm({
    defaultValues: {
      privateLessonAllowance: '',
      baseSalary: '',
      groupLessonAllowance: '',
      commission: '',
    },
  });
  const [query, setQuery] = useState({
    startDate: formattedDate,
    endDate: formattedDate,
  });
  const [isEndDate, setIsEndDate] = useState(false);
  const { search } = useLocation();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const salaryId = searchParams.get('salaryId');
  const name = searchParams.get('name');
  const { user } = useSelector(state => state.user);

  const submit = async values => {
    let response = null;
    try {
        // 날짜 유효성 검증
        if (!validateDateRange(query.startDate, query.endDate)) {
          toast.error('기간을 다시 확인해주세요');
          return;
        }

      if (salaryId) {
        response = await updateSalary(
          salaryId,
          {
            ...query,
            ...values,
            employeeId: id,
            endDate: !isEndDate ? query.endDate : null,
            privateLessonAllowance: formatValue(values.privateLessonAllowance),
            baseSalary: formatValue(values.baseSalary),
            groupLessonAllowance: formatValue(values.groupLessonAllowance),
            commission: formatValue(values.commission),
          },
          user.token,
        );
        toast.success(t('Salary information has been updated'));
      } else {
        response = await createSalary(
          {
            ...query,
            ...values,
            employeeId: id,
            endDate: !isEndDate ? query.endDate : null,
            privateLessonAllowance: formatValue(values.privateLessonAllowance),
            baseSalary: formatValue(values.baseSalary),
            groupLessonAllowance: formatValue(values.groupLessonAllowance),
            commission: formatValue(values.commission),
          },
          user.token,
        );
        toast.success(t('Salary information has been registered'));
      }

      navigate('/salary');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const formatValue = value => {
    if (value === '' || value === undefined || value === null) {
      return '0'; // Return '0' for empty or invalid values
    }
    return String(value).replace(/,/g, ''); // Convert to string and remove commas
  };

  const getSalary = async () => {
    try {
      const response = await getSalaryById(id, user.token);
      const filteredResponse = response.filter(res => res.salaryId === Number(salaryId));
      reset({ ...filteredResponse[0] });
      setQuery({ ...query, startDate: filteredResponse[0].startDate, endDate: filteredResponse[0].endDate });
      setIsEndDate(filteredResponse[0].endDate ?? true);
    } catch (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    if (salaryId) {
      getSalary();
    }
  }, []);

  const privateLessonAllowance = watch('privateLessonAllowance');
  const baseSalary = watch('baseSalary');
  const groupLessonAllowance = watch('groupLessonAllowance');

  // Utility function to format numbers with commas
  const formatNumber = value => {
    if (value === undefined || value === null) return '';
    const strValue = String(value).replace(/,/g, ''); // Remove existing commas
    return strValue.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // Format with commas
  };

  // Watch and format privateLessonAllowance
  useEffect(() => {
    if (privateLessonAllowance !== undefined && privateLessonAllowance !== null) {
      setValue('privateLessonAllowance', formatNumber(privateLessonAllowance));
    }
  }, [privateLessonAllowance, setValue]);

  // Watch and format baseSalary
  useEffect(() => {
    if (baseSalary !== undefined && baseSalary !== null) {
      setValue('baseSalary', formatNumber(baseSalary));
    }
  }, [baseSalary, setValue]);

  // Watch and format groupLessonAllowance
  useEffect(() => {
    if (groupLessonAllowance !== undefined && groupLessonAllowance !== null) {
      setValue('groupLessonAllowance', formatNumber(groupLessonAllowance));
    }
  }, [groupLessonAllowance, setValue]);

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '5px',
        padding: '40px',
        width: '1080px',
        minHeight: '560px',
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', opacity: 2 }}>
        <Box sx={{ display: 'flex' }}>
          <Link
            style={{
              textDecoration: 'none',
              color: '#3a3a3a',
              display: 'flex',
            }}
            to={`/salary/${id ?? ''}`}>
            <svg id="뒤로가기" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path id="Path_6534" data-name="Path 6534" d="M0,0H24V24H0Z" fill="none" />
              <circle
                id="Ellipse_96"
                data-name="Ellipse 96"
                cx="11"
                cy="11"
                r="11"
                transform="translate(1 1)"
                fill="#3a3a3a"
              />
              <path
                id="Path_6535"
                data-name="Path 6535"
                d="M6.393,8.579H14V9.865H6.393l3.352,3.448-.884.909L4,9.222l4.861-5,.884.909Z"
                transform="translate(3 2.778)"
                fill="#fff"
              />
            </svg>
            <Typography sx={{ fontSize: '18px', ml: '8px', fontWeight: '550' }}>
              {t('Create Salary Details')}
            </Typography>
          </Link>
        </Box>
        <Link
          style={{
            color: theme.palette.primary.main,
            textDecoration: 'none',
            fontWeight: 550,
            fontSize: '14px',
          }}
          to="/salary">
          {t('List')}
        </Link>
      </Box>
      <hr style={{ border: '1px solid #e1e1e1', marginTop: '19px' }} />
      <Typography sx={{ fontSize: '22px', fontWeight: 'bold', mt: '20px' }}>{name || ''}</Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pt: '33px',
          '.css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input': { padding: '11px 14px' },
          gap: '16px',
        }}>
        <Typography sx={{ width: '100px', fontWeight: 500 }}>{t('Pay Period')}</Typography>
        {/* <Typography sx={{ fontWeight: 500 }}>{t('Start Date')}</Typography> */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CommonDatePicker
            placeholder="시작일"
            sx={{
                width: '150px',
                input: {
                      paddingRight: '0px!important',
                  },
            }}
            value={query.startDate}
            // disabled={isEndDate}
            onChange={n =>
              setQuery({
                ...query,
                startDate: dateConverter(n),
              })
            }
          />
          <Typography sx={{ fontSize: '18px', ml: '10px', mr: '10px' }}>-</Typography>
          {/* <Typography sx={{ fontWeight: 500, ml: '10px', mr: '10px' }}>{t('End Date')}</Typography> */}
          <CommonDatePicker
            placeholder="종료일"
            sx={{
                width: '150px',
                input: {
                      paddingRight: '0px!important',
                  },
            }}
            value={query.endDate}
            disabled={isEndDate}
            onChange={n =>
              setQuery({
                ...query,
                endDate: dateConverter(n),
              })
            }
            minDate={query.startDate}
          />
        </Box>

        <FormControlLabel
          sx={{ ml: '22px' }}
          control={
            <CustomCheckbox
              color="primary"
              name={'row.name'}
              checked={isEndDate}
              onChange={e => {
                setIsEndDate(!isEndDate);
              }}
            />
          }
          label={t('No end date')}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: '20px',
          '.css-1g24dm6-MuiInputBase-input-MuiOutlinedInput-input': {
            padding: '11px 14px',
          },
        }}>
        <Typography sx={{ width: '100px', fontWeight: 500 }}>{t('Basic Pay')}</Typography>
        <FormInputText
          color="info"
          sx={{ width: '375px' }}
          name="baseSalary"
          placeholder="금액을 입력해 주세요"
          id="outlined-search"
          type="search"
          control={control}
          setValue={setValue}
        />
        <Typography>{t('USD')}</Typography>
        <Typography>{t('3.3% statutory deduction applied')} </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: '20px',
          '.css-1g24dm6-MuiInputBase-input-MuiOutlinedInput-input': {
            padding: '11px 14px',
          },
        }}>
        <Typography sx={{ width: '100px', fontWeight: 500 }}>{t('1-on-1 Pay')}</Typography>
        <FormInputText
          color="info"
          sx={{ width: '375px' }}
          name="privateLessonAllowance"
          placeholder="금액을 입력해 주세요"
          id="outlined-search"
          type="search"
          setValue={setValue}
          control={control}
        />
        <Typography>{t('USD')}</Typography>
        <Typography>{t('Set a fixed allowance for one private lesson')}</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: '20px',
          '.css-1g24dm6-MuiInputBase-input-MuiOutlinedInput-input': {
            padding: '11px 14px',
          },
        }}>
        <Typography sx={{ width: '100px', fontWeight: 500 }}>{t('Group Pay')}</Typography>
        <FormInputText
          color="info"
          name="groupLessonAllowance"
          sx={{ width: '375px' }}
          placeholder="금액을 입력해 주세요"
          id="outlined-search"
          type="search"
          control={control}
          setValue={setValue}
        />
        <Typography>{t('USD')}</Typography>
        <Typography>{t('Set a fixed allowance for one group lesson')}</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: '20px',
          '.css-1g24dm6-MuiInputBase-input-MuiOutlinedInput-input': {
            padding: '11px 14px',
          },
        }}>
        <Typography sx={{ minWidth: '100px', fontWeight: 500 }}>{t('Commission')}</Typography>
        <FormInputText
          color="info"
          sx={{ width: '375px' }}
          placeholder="요율을 입력해 주세요"
          id="outlined-search"
          type="search"
          name="commission"
          control={control}
        />
        <Typography>%</Typography>
        <Typography>
          {t('The employee earns a certain percentage of their gross salary as an additional compensation')}
        </Typography>
      </Box>

      <hr style={{ border: '1px solid #e1e1e1', marginTop: '30px' }} />
      <Box sx={{ display: 'flex', justifyContent: 'end', mt: '12px', mb: '20px' }}>
        <Button
          sx={{
            mr: '6px',
            width: '100px',
            height: '36px',
            background: '#f7f7f7',
            color: '#9a9a9a',
            borderRadius: '15px',
            p: '12px 37px 11px 37px',
            '&:hover': { background: '#ddd' },
            fontSize: '13px',
          }}>
          {t('Cancel')}
        </Button>
        <Button
          sx={{
            width: '137px',
            height: '36px',
            background: '#faf7fe',
            borderRadius: '15px',
            p: '12px 56px 11px 56px',
            '&:hover': { background: '#0F7F85', color: '#fff' },
          }}
          onClick={handleSubmit(submit)}>
          {t('Save')}
        </Button>
      </Box>
    </div>
  );
};

export default PayrollCreate;
